import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { SiteSettings, SiteSettingsUpdateReq } from "@etheria/shared";
import { api } from "../lib/api";
import { qk } from "./keys";

/**
 * Domaine RÉGLAGES de site (SITE_SPEC — paramètres globaux : IP serveur, Discord, réseaux sociaux).
 *
 * PUBLIC  : sous-ensemble sûr (serverIp, discordUrl, socials) affiché aux visiteurs.
 * ADMIN   : mise à jour du réglage complet (role-gated), persisté dans `web_settings`.
 */

/** Sous-ensemble public des réglages (jamais de champs sensibles). */
export type PublicSiteSettings = Pick<SiteSettings, "serverIp" | "discordUrl" | "socials">;

// ── PUBLIC ────────────────────────────────────────────────────────────────────

/** GET /api/settings — réglages publics (IP serveur, Discord, réseaux). */
export function useSiteSettings() {
  return useQuery({
    queryKey: qk.settings.public(),
    queryFn: ({ signal }) => api.get<PublicSiteSettings>("/settings", { signal }),
    staleTime: 60_000,
  });
}

// ── ADMIN ─────────────────────────────────────────────────────────────────────

/** PUT /api/admin/settings — met à jour les réglages (admin). Renvoie le réglage complet. */
export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SiteSettingsUpdateReq) =>
      api.put<SiteSettings>("/admin/settings", body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.settings.all });
    },
  });
}
